import React, { useEffect, useState } from 'react';
import { Spin, Avatar, Tooltip } from 'antd';
import { fetchLhOrderTrackingDetails } from "../../../api/OrdersApi";
import { renderNotification } from "../../../components/orders/OrderTypeIcons";
import {
  LhArrivalIcon,
  LhInTransitIcon,
  LhPendingIcon,
  LhFinalStopArrivedIcon,
  PendingOriginIcon,
  PendingDestinationIcon,
  InTransitDestinationIcon,
  LhOrderPageIcon,
  LhBadgeCompleteIcon,
  LhBadgeInTransitIcon,
  LhBadgePendingIcon
} from "../../../common/CustomIcon";
import "./TrackingStyles.scss";
import {
  extractCalendarYmd,
  formatCalendarDisplay,
  formatLinehaulDatetime,
  formatStopDwellValue,
  formatStopTrackingDatetime,
  isInboundOutboundLoc,
  isWarehouseOnlyLinehaul,
  resolveStopDelayInfo,
  resolveTerminalDwellLabel,
  resolveTrackingStatus,
  stopHasTrackingField,
} from "./LhTrackingCard";

export const RouteTracker = ({ orderResponse = {}, activeKey }) => {
  const [lhData, setLhData] = useState(null);
  const [lhDetails, setLhDetails] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchTrackingData = async () => {
    if (!orderResponse?.id) return;

    setLoading(true);
    try {
      const response = await fetchLhOrderTrackingDetails(orderResponse.id);
      if (response && (response.success || response.status === 200)) {
        const data = response.data || response;
        setLhData(data);
        const terminals = Array.isArray(data?.terminals)
          ? data.terminals
          : Array.isArray(data?.linehaul_tracking_details)
          ? data.linehaul_tracking_details
          : Array.isArray(data)
          ? data
          : [];
        setLhDetails([...terminals].sort((a, b) => (a.sequence_no ?? 0) - (b.sequence_no ?? 0)));
      } else {
        renderNotification("Something went wrong while fetching tracking details", "error");
      }
    } catch (error) {
      console.error("Error fetching tracking data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (orderResponse?.id) {
      if (activeKey === 'tracking' || !activeKey) {
        fetchTrackingData();
      }
    }
  }, [orderResponse?.id, activeKey]);

  const orderLabel =
    orderResponse?.order_label
    || orderResponse?.customer_order_details?.order_label
    || "";
  const warehouseOnlyLh = isWarehouseOnlyLinehaul(orderLabel);

  const formatAddress = (address) => {
    if (!address) return "";
    if (typeof address === 'string') return address;
    const parts = [];
    if (address.address_line1) parts.push(address.address_line1);
    if (address.city) parts.push(address.city);
    if (address.state) parts.push(address.state);
    if (address.zipcode) parts.push(address.zipcode);
    return parts.join(", ");
  };

  const getInitials = (name = "") => {
    const parts = name.trim().split(" ");
    if (parts.length === 0 || !parts[0]) return "";
    if (parts.length === 1) {
      return parts[0][0]?.toUpperCase() || "";
    }
    return (
      (parts[0][0] || "") + (parts[parts.length - 1][0] || "")
    ).toUpperCase();
  };

  const formatEventTime = (dateStr) => formatLinehaulDatetime(dateStr, "");

  // Normalized status logic
  const getNormalizedStatus = (stop) => {
    const rawTrackingStatus = (stop.tracking_status || "").toUpperCase();

    if (rawTrackingStatus === "COMPLETED" || rawTrackingStatus === "COMPLETE") {
      return "COMPLETED";
    }
    if (rawTrackingStatus === "ARRIVED") {
      return "ARRIVED";
    }
    if (
      rawTrackingStatus === "IN TRANSIT" ||
      rawTrackingStatus === "IN_TRANSIT" ||
      rawTrackingStatus === "ACTIVE"
    ) {
      return "IN TRANSIT";
    }
    return "PENDING";
  };

  const getIoStatus = (stop) => {
    const raw = resolveTrackingStatus(stop);
    return raw === "COMPLETED" || raw === "COMPLETE" ? "COMPLETED" : "PENDING";
  };

  const getIoAwareStatus = (stop, index) => {
    const loc = String(stop?.type_of_loc || "").toUpperCase();
    if (!isInboundOutboundLoc(loc)) return getNormalizedStatus(stop);
    return getIoStatus(stop);
  };

  // Connector between this stop and the next is the trip TO the next stop.
  // IN_TRANSIT on a stop means the freight is arriving there, not that it has left.
  const getConnectorClass = (_stop, nextStop, nextIndex) => {
    if (!nextStop) return "connector-gray";

    const nextStatus = getIoAwareStatus(nextStop, nextIndex);
    if (nextStatus === "COMPLETED" || nextStatus === "ARRIVED") {
      return "connector-green";
    }
    if (nextStatus === "IN TRANSIT") {
      return "connector-yellow";
    }
    return "connector-gray";
  };

  if (loading) {
    return (
      <div className="route-tracker-loading">
        <Spin size="large" tip="Loading Route Tracking Details..." />
      </div>
    );
  }

  if (!lhDetails || lhDetails.length === 0) {
    return (
      <div className="empty-tracking-state">
        <div className="empty-state-container">
          <div className="empty-state-content">
            <h3 className="empty-state-title">No Tracking Information Available</h3>
            <p className="empty-state-description">
              Linehaul tracking details are not yet available for this order.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const firstWarehouseIndex = lhDetails.findIndex((s) => !isInboundOutboundLoc(s?.type_of_loc));
  const lastWarehouseIndex = lhDetails.reduce((last, s, i) => (
    isInboundOutboundLoc(s?.type_of_loc) ? last : i
  ), -1);

  return (
    <div className="route-tracker-container">
      <h4 className="route-tracker-title">Tracking</h4>
      <div className="route-tracker-wrapper">
        {lhDetails.map((stop, index) => {
          const isLast = index === lhDetails.length - 1;
          const nextStop = lhDetails[index + 1];

          const locTypeUpper = String(stop.type_of_loc || "").toUpperCase();
          const isIoStop = isInboundOutboundLoc(locTypeUpper);
          const status = getIoAwareStatus(stop, index);
          const isCompleted = status === "COMPLETED";
          const isArrived = !isIoStop && status === "ARRIVED";
          const isInTransit = !isIoStop && status === "IN TRANSIT";
          const isPending = status === "PENDING";

          let cardVariant = "pending";
          let statusText = "PENDING";

          if (isCompleted) {
            cardVariant = "complete";
            statusText = "COMPLETE";
          } else if (isArrived) {
            cardVariant = "arrived";
            statusText = "ARRIVED";
          } else if (isInTransit) {
            cardVariant = "in-transit";
            statusText = "IN TRANSIT";
          }

          const isPickupStop = !isIoStop && (
            locTypeUpper === "PICKUP" || index === firstWarehouseIndex
          );
          const isDeliveryStop = !isIoStop && index !== firstWarehouseIndex && (
            locTypeUpper === "DELIVERY" || index === lastWarehouseIndex
          );

          // Play pin on Pickup, hole pin on warehouses, square stop pin on Delivery.
          let PinIconComponent = LhPendingIcon;

          if (isPickupStop) {
            if (isCompleted) {
              PinIconComponent = () => <PendingOriginIcon color="#13B57B" />;
            } else if (isArrived) {
              PinIconComponent = () => <InTransitDestinationIcon color="#FFC120" />;
            } else if (isInTransit) {
              PinIconComponent = LhInTransitIcon;
            } else {
              PinIconComponent = () => <PendingOriginIcon color="#555F67" />;
            }
          } else if (isDeliveryStop) {
            if (isCompleted) {
              PinIconComponent = () => <LhFinalStopArrivedIcon color="#13B57B" />;
            } else if (isArrived) {
              PinIconComponent = () => <InTransitDestinationIcon color="#FFC120" />;
            } else if (isInTransit) {
              PinIconComponent = () => <InTransitDestinationIcon color="#FFC120" />;
            } else {
              PinIconComponent = () => <PendingDestinationIcon color="#555F67" />;
            }
          } else {
            if (isCompleted) {
              PinIconComponent = () => <LhArrivalIcon color="#13B57B" />;
            } else if (isArrived) {
              PinIconComponent = () => <InTransitDestinationIcon color="#FFC120" />;
            } else if (isInTransit) {
              PinIconComponent = LhInTransitIcon;
            } else {
              PinIconComponent = () => <LhPendingIcon color="#555F67" />;
            }
          }

          let BadgeIconComponent = LhBadgePendingIcon;
          if (isCompleted) {
            BadgeIconComponent = LhBadgeCompleteIcon;
          } else if (isArrived) {
            BadgeIconComponent = (props) => <LhBadgeCompleteIcon color="#CC9200" style={{ color: "#CC9200", ...props?.style }} {...props} />;
          } else if (isInTransit) {
            BadgeIconComponent = LhBadgeInTransitIcon;
          }

          const delayInfo = isIoStop ? null : resolveStopDelayInfo(stop, status);
          const dwellTime = resolveTerminalDwellLabel(stop, lhData, orderResponse);
          const onHandFieldValue = stop.on_hand_datetime ?? stop.onHandDatetime;
          const outboundDockFieldValue = stop.outbound_dock_datetime ?? stop.outboundDockDatetime;

          const stopTitle = isIoStop
            ? locTypeUpper
            : warehouseOnlyLh
            ? (stop.warehouse_code || stop.warehouse_name || stop.location_name || "Unknown Stop")
            : locTypeUpper === "PICKUP"
            ? "Pickup"
            : locTypeUpper === "DELIVERY"
            ? "Delivery"
            : (stop.warehouse_name || stop.location_name || stop.warehouse_code || "Unknown Stop");
          const stopAddress = isIoStop ? "" : (stop.warehouse_address || formatAddress(stop.l_address));

          return (
            <div className="stop-row" key={`${locTypeUpper || "WH"}-${stop.warehouse_id || stop.location_id || index}-${index}`}>
              {!isLast && (
                <div className={`timeline-connector ${getConnectorClass(stop, nextStop, index + 1)}`} />
              )}

              <div className={`tracking-card variant-${cardVariant}`}>
                {/* Card Header Row */}
                <div className="card-header">
                  <div className="pin-icon-wrapper">
                    <PinIconComponent />
                  </div>

                  <div className="header-main">
                    <span className="stop-title">{stopTitle}</span>
                    {stopAddress ? <span className="stop-address">{stopAddress}</span> : null}
                  </div>
                  
                  <div className="header-badges">
                    {stop.manifest_number && (
                      <span className="order-id-pill">
                        <LhOrderPageIcon style={{ marginRight: 6, fontSize: '14px', verticalAlign: 'middle' }} />
                        {stop.manifest_number}
                      </span>
                    )}
                    {delayInfo && (
                      <Tooltip
                        title={(
                          <span style={{ whiteSpace: "pre-line" }}>
                            {delayInfo.tooltip}
                          </span>
                        )}
                      >
                        <span
                          className={`delay-text ${delayInfo.isLate ? "text-error" : "text-success"}`}
                          style={{ cursor: "help" }}
                        >
                          {delayInfo.text}
                        </span>
                      </Tooltip>
                    )}
                    <span className={`status-badge badge-${cardVariant}`}>
                      <BadgeIconComponent style={{ marginRight: 6, display: 'inline-flex', alignSelf: 'center', fontSize: '12px' }} />
                      {statusText}
                    </span>
                  </div>
                </div>

                {/* Card Metadata Row */}
                <div className="card-metadata">
                  <div className="metadata-timeline">
                    {isIoStop ? (
                      <>
                        {stopHasTrackingField(stop, "on_hand_datetime", "onHandDatetime") && (
                          <div className="timeline-field">
                            <span className="field-label">ON HAND</span>
                            <span className="field-value">
                              {formatStopTrackingDatetime(onHandFieldValue)}
                            </span>
                          </div>
                        )}
                        {stopHasTrackingField(stop, "outbound_dock_datetime", "outboundDockDatetime") && (
                          <div className="timeline-field">
                            <span className="field-label">DEL</span>
                            <span className="field-value">
                              {formatStopTrackingDatetime(outboundDockFieldValue)}
                            </span>
                          </div>
                        )}
                        {stopHasTrackingField(stop, "dwell_time") && (
                          <div className="timeline-field">
                            <span className="field-label">DWELL</span>
                            <span className="field-value">
                              {formatStopDwellValue(stop)}
                            </span>
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                    {stopHasTrackingField(stop, "on_hand_datetime", "onHandDatetime") && (
                      <div className="timeline-field">
                        <span className="field-label">ON HAND</span>
                        <span className="field-value">
                          {formatStopTrackingDatetime(onHandFieldValue)}
                        </span>
                      </div>
                    )}
                    {stopHasTrackingField(stop, "outbound_dock_datetime", "outboundDockDatetime") && (
                      <div className="timeline-field">
                        <span className="field-label">DEL</span>
                        <span className="field-value">
                          {formatStopTrackingDatetime(outboundDockFieldValue)}
                        </span>
                      </div>
                    )}
                    {isCompleted && (
                      <>
                        <div className="timeline-field">
                          <span className="field-label">{index === firstWarehouseIndex ? "PICKUP BY" : "DELIVER BY"}</span>
                          <span className="field-value">
                            {formatEventTime(stop.actual_departure || stop.actual_arrival || stop.linehaul_service_date || stop.eta) || "N/A"}
                          </span>
                        </div>
                        <div className="timeline-field">
                          <span className="field-label">ARR</span>
                          <span className="field-value">
                            {formatEventTime(stop.actual_arrival) || "N/A"}
                          </span>
                        </div>
                        <div className="timeline-field">
                          <span className="field-label">DEP</span>
                          <span className="field-value">
                            {formatEventTime(stop.actual_departure) || "N/A"}
                          </span>
                        </div>
                        {stopHasTrackingField(stop, "dwell_time") ? (
                          <div className="timeline-field">
                            <span className="field-label">DWELL</span>
                            <span className="field-value">
                              {formatStopDwellValue(stop)}
                            </span>
                          </div>
                        ) : dwellTime ? (
                          <div className="timeline-field">
                            <span className="field-label">DWELL</span>
                            <span className="field-value">{dwellTime}</span>
                          </div>
                        ) : null}
                      </>
                    )}
                    {isArrived && (
                      <>
                        <div className="timeline-field">
                          <span className="field-label">ARR</span>
                          <span className="field-value">
                            {formatEventTime(stop.actual_arrival) || "N/A"}
                          </span>
                        </div>
                        {stop.actual_departure && (
                          <div className="timeline-field">
                            <span className="field-label">DEP</span>
                            <span className="field-value">
                              {formatEventTime(stop.actual_departure)}
                            </span>
                          </div>
                        )}
                        {stop.eta && (
                          <div className="timeline-field">
                            <span className="field-label">ETA</span>
                            <span className="field-value">
                              {formatEventTime(stop.eta)}
                            </span>
                          </div>
                        )}
                      </>
                    )}
                    {isInTransit && (
                      <div className="timeline-field">
                        <span className="field-label">ETA</span>
                        <span className="field-value">
                          {formatEventTime(stop.eta) || "N/A"}
                        </span>
                      </div>
                    )}
                    {isPending && (
                      <>
                        {index === lastWarehouseIndex ? (
                          <>
                            <div className="timeline-field">
                              <span className="field-label">DELIVER BY</span>
                              <span className="field-value">
                                {formatEventTime(stop.eta || orderResponse?.delivery_by) || "N/A"}
                              </span>
                            </div>
                            <div className="timeline-field">
                              <span className="field-label">ETA</span>
                              <span className="field-value">
                                {formatEventTime(stop.eta) || "N/A"}
                              </span>
                            </div>
                            <div className="timeline-field">
                              <span className="field-label">SERVICE DATE</span>
                              <span className="field-value">
                                {extractCalendarYmd(stop.linehaul_service_date)
                                  ? formatCalendarDisplay(stop.linehaul_service_date)
                                  : "N/A"}
                              </span>
                            </div>
                          </>
                        ) : (
                          <div className="timeline-field">
                            <span className="field-label">ETA</span>
                            <span className="field-value">
                              {formatEventTime(stop.eta) || "N/A"}
                            </span>
                          </div>
                        )}
                      </>
                    )}
                      </>
                    )}
                  </div>

                  {!isIoStop && stop.driver_name && (
                    <div className="metadata-driver">
                      <Tooltip title={stop.driver_name}>
                        <Avatar
                          size="small"
                          className="driver-avatar"
                        >
                          {getInitials(stop.driver_name)}
                        </Avatar>
                      </Tooltip>
                      <span className="driver-name-text">{stop.driver_name}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default RouteTracker;
